import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import * as yup from 'yup';
import {
  Alert, Box, Button, Flex, Group, LoadingOverlay, Menu,
  Modal, Stack, Text, TextInput, UnstyledButton, useMantineTheme,
} from '@mantine/core';
import { useForm, yupResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import {
  IconAlertCircle, IconCheck, IconPlus, IconSelector,
} from '@tabler/icons';

import { AppActionType, AppContext } from '../contexts/app';
import { AuthContext } from '../contexts/auth';
import { Project, ProjectCreateDTO } from '../models';
import { useApi } from '../hooks/use-api';
import { handleFormErrors, handleNonFormErrors, handleFormClientValidationErrors } from '../helpers/errors';
import { useNavigateRef } from '../hooks/use-navigate-ref';
import { grp } from '../routes';

export const ProjectSelector: React.FC<{ setNavbarOpened: (value: React.SetStateAction<boolean>) => void }> = ({ setNavbarOpened }) => {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const { appState, appDispatch } = useContext(AppContext);
  const { authState } = useContext(AuthContext);
  const navigate = useNavigateRef();
  const theme = useMantineTheme();

  const [projectCreateCall, projectCreateIsProcessing] = useApi('projects.create');
  const [projectListCall, projectListIsLoading] = useApi('projects.list', { loadingInitialState: true });

  const formSchema = yup.object({
    name: yup.string().label('Project name')
      .required(),
  });

  const form = useForm({
    initialValues: {
      name: '',
    },
    validate: yupResolver(formSchema),
  });

  const handleFormSubmit = async (values: typeof form.values) => {
    setErrorMsg(null);

    try {
      const fd = new ProjectCreateDTO();
      fd.name = values.name;

      const data = await projectCreateCall({ params: fd });

      if (data.project) {
        // update projects
        setProjects([...(projects || []), data.project]);

        // update current project id
        appDispatch({
          type: AppActionType.CHANGE_CURRENT_PROJECT,
          payload: {
            projectId: data.project?.id,
            name: data.project?.name,
            role: 'owner',
          },
        });

        // reset form
        form.reset();

        // close modal
        setModalIsOpen(false);

        // redirect to dashboard
        navigate.current(grp('homepage'));

        showNotification({
          color: 'green',
          message: 'Project created successfully.',
        });
      }
    } catch (error) {
      handleFormErrors(error, form.setErrors, setErrorMsg);
    }
  };

  const changeCurrentProject = (projectId: string) => {
    const project = projects?.find((p) => p.id === projectId);
    if (!project) {
      return;
    }

    appDispatch({
      type: AppActionType.CHANGE_CURRENT_PROJECT,
      payload: {
        projectId,
        name: project.name,
        role: project.userId === authState.user?.id ? 'owner' : 'member',
      },
    });

    setNavbarOpened(false);

    navigate.current(grp('homepage'));
  };

  const loadProjects = useCallback(async () => {
    try {
      const projectListData = await projectListCall();
      if (projectListData.projects) {
        setProjects(projectListData.projects);
      }
    } catch (error) {
      handleNonFormErrors(error, (message) => {
        showNotification({ color: 'red', message });
      });
    }
  }, [projectListCall]);

  const ownedProjects = projects?.filter((project) => project.userId === authState.user?.id) || [];

  const memberProjects = projects?.filter((project) => project.userId !== authState.user?.id) || [];

  useEffect(() => {
    loadProjects();
  }, [loadProjects, appState.currentProjectLastUpdate, appState.acceptedProjectInvitationId]);

  useEffect(() => {
    const cp = projects?.find((p) => p.id === appState.currentProjectId) || projects?.[0];
    if (cp) {
      setCurrentProject(cp);
      if (!appState.currentProjectId) {
        appDispatch({
          type: AppActionType.CHANGE_CURRENT_PROJECT,
          payload: {
            projectId: cp.id,
            name: cp.name,
            role: cp.userId === authState.user?.id ? 'owner' : 'member',
          },
        });
      }
    }
  }, [appDispatch, appState.currentProjectId, authState.user?.id, projects]);

  return (
    <>
      <Menu
        shadow="md"
        width="90%"
        disabled={projectListIsLoading}
      >
        <Menu.Target>
          <Box pos="relative">
            <LoadingOverlay
              visible={projectListIsLoading}
              sx={{ borderRadius: theme.radius.md }}
              loaderProps={{ size: 'xs', color: 'dark' }}
              overlayColor={
                theme.colorScheme === 'dark'
                  ? undefined
                  : theme.colors.brand[0]
              }
            />

            <UnstyledButton
              w="100%"
              p="xs"
            >
              <Group noWrap position="apart">
                <Flex direction="column" miw={0}>
                  <Text color="dimmed" size="xs">
                    Current Project
                  </Text>

                  <Text size="sm" weight={600} truncate>
                    {currentProject?.name || '-'}
                  </Text>
                </Flex>

                <Flex>
                  <IconSelector size={24} stroke={1.5} />
                </Flex>
              </Group>
            </UnstyledButton>
          </Box>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Owned</Menu.Label>
          {ownedProjects.sort((a, b) => a.name.localeCompare(b.name))
            .map((project) => (
              <Menu.Item
                key={project.id}
                color={project.id === currentProject?.id ? 'brand' : undefined}
                bg={project.id === currentProject?.id ? 'brand.0' : undefined}
                onClick={() => { changeCurrentProject(project.id); }}
                rightSection={project.id === currentProject?.id ? <IconCheck size={14} /> : undefined}
              >
                {project.name}
              </Menu.Item>
            ))}

          <Menu.Divider />

          {memberProjects.length > 0 && (
            <>
              <Menu.Label>Member</Menu.Label>
              {memberProjects.sort((a, b) => a.name.localeCompare(b.name))
                .map((project) => (
                  <Menu.Item
                    key={project.id}
                    color={project.id === currentProject?.id ? 'brand' : undefined}
                    bg={project.id === currentProject?.id ? 'brand.0' : undefined}
                    onClick={() => { changeCurrentProject(project.id); }}
                    rightSection={project.id === currentProject?.id ? <IconCheck size={14} /> : undefined}
                  >
                    {project.name}
                  </Menu.Item>
                ))}
              <Menu.Divider />
            </>
          )}

          <Menu.Item
            icon={<IconPlus size={14} />}
            onClick={() => { setModalIsOpen(true); }}
          >
            Create new project
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <Modal
        opened={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        centered
        title="Create New Project"
      >
        <form onSubmit={form.onSubmit(handleFormSubmit, handleFormClientValidationErrors)}>
          <Stack spacing="lg">

            {errorMsg ? <Alert mb="sm" icon={<IconAlertCircle size={16} />} color="red">{errorMsg}</Alert> : null}

            <TextInput
              label="Project Name"
              placeholder="Enter project name"
              {...form.getInputProps('name')}
            />

            <Group position="right">
              <Button
                type="submit"
                loading={projectCreateIsProcessing}
              >
                Save
              </Button>
            </Group>

          </Stack>
        </form>
      </Modal>
    </>
  );
};
